// Deterministic replays of one streamed project digest, for Lesson 3.9 ("Ten Seconds of
// Blank Screen"). Every chunk, time, piece, event and row below was recorded from real runs
// on 8 October 2026. The graphics replay those recordings and never call a model.

import { JSON_REPLY } from './output-models';

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const secondsLabel = (at: number) => `${at.toFixed(2)} s`;
export const tokensLabel = (prompt: number, completion: number) =>
  `${prompt} prompt / ${completion} completion`;
export const NOTHING_YET = '(nothing yet)';
export const NOT_YET = 'not yet';

// --- stream-events: the seven data: lines of the Apollo digest -----------------------------

export type RecordedChunk =
  | { at: number; kind: 'role' }
  | { at: number; kind: 'content'; content: string; finishReason: 'stop' | null }
  | { at: number; kind: 'usage' }
  | { at: number; kind: 'done' };
const content = (at: number, text: string, finishReason: 'stop' | null = null): RecordedChunk => ({
  at,
  kind: 'content',
  content: text,
  finishReason,
});
/** The first words, on the wire at 1.05 s. */
export const DIGEST_FIRST_PIECE =
  'Maya and Jo have completed the demo preparation and room booking, but several follow-up tasks remain open. The';
/** The seven lines, with the time from the request in seconds. */
export const RECORDED_CHUNKS: readonly RecordedChunk[] = [
  { at: 0.85, kind: 'role' },
  content(1.05, DIGEST_FIRST_PIECE),
  content(
    1.26,
    " team needs to review Maya's notes and check examples, while Ravi still needs to write the release note",
  ),
  content(
    1.49,
    '. Jo is waiting to receive the parking map, and the agenda has already been sent to Ravi',
  ),
  content(
    1.49,
    '. Outstanding logistical items include ordering name badges, checking the projector, and arranging invoice payment.',
    'stop',
  ),
  { at: 1.5, kind: 'usage' },
  { at: 1.5, kind: 'done' },
];
export const CHUNK_COUNT = RECORDED_CHUNKS.length;
export const STREAM_USAGE = { prompt: 182, completion: 82, total: 264 } as const;
export const ROLE_LINE =
  'data: {"choices":[{"delta":{"content":"","role":"assistant"},"finish_reason":null,"index":0}],"object":"chat.completion.chunk"}';
export const USAGE_LINE =
  'data: {"choices":[],"object":"chat.completion.chunk","usage":{"completion_tokens":82,"prompt_tokens":182,"total_tokens":264}}';
export const DONE_LINE = 'data: [DONE]';
/** The line as the lesson shows it: content lines abridged to delta, finish_reason and index. */
export function chunkLine(chunk: RecordedChunk): string {
  switch (chunk.kind) {
    case 'role':
      return ROLE_LINE;
    case 'usage':
      return USAGE_LINE;
    case 'done':
      return DONE_LINE;
    case 'content':
      return `data: ${JSON.stringify({
        choices: [
          { delta: { content: chunk.content }, finish_reason: chunk.finishReason, index: 0 },
        ],
        object: 'chat.completion.chunk',
      })}`;
  }
}
/** The content deltas joined, in order: what the page shows after these chunks. */
export const assembledText = (chunks: readonly RecordedChunk[]) =>
  chunks.map((chunk) => (chunk.kind === 'content' ? chunk.content : '')).join('');
export const STREAMED_DIGEST = assembledText(RECORDED_CHUNKS);
/** The same request without stream: true, one reply. */
export const PLAIN_AT = 2.58;
export const PLAIN_USAGE = { prompt: 182, completion: 89 } as const;
export const PLAIN_DIGEST =
  "Maya and Jo have completed the demo preparation and room booking, and the agenda has been sent to Ravi. Ravi is now responsible for writing the release note, while several logistical tasks remain open, including ordering name badges and checking the projector. The team needs to read Maya's notes and check the examples to move forward. A few administrative items, such as arranging invoice payment and asking the library about the room, are still pending.";
export const BLANK_TIMELINE = '0.0 s … 2.58 s: nothing on the page';
export const PLAIN_TIMELINE = `${BLANK_TIMELINE}\n${secondsLabel(PLAIN_AT)}: status 200, the whole reply, usage ${tokensLabel(PLAIN_USAGE.prompt, PLAIN_USAGE.completion)}`;
export interface EventsState {
  streaming: boolean;
  /** How many of the seven lines have arrived; 0 before the first press. */
  revealed: number;
}
export const initialEvents = (): EventsState => ({ streaming: false, revealed: 0 });
export type EventsAction = { type: 'toggle' } | { type: 'next' } | { type: 'reset' };
export const canRevealNext = (state: EventsState) =>
  state.streaming && state.revealed < CHUNK_COUNT;
export function eventsTransition(state: EventsState, action: EventsAction): EventsState {
  switch (action.type) {
    case 'reset':
      return initialEvents();
    case 'toggle':
      return { streaming: !state.streaming, revealed: 0 };
    case 'next':
      return canRevealNext(state) ? { ...state, revealed: state.revealed + 1 } : state;
  }
}
export const revealedChunks = (state: EventsState): readonly RecordedChunk[] =>
  RECORDED_CHUNKS.slice(0, state.revealed);
/** The Page so far panel: the assembled text, or a note that there is none. */
export const pageText = (state: EventsState) => assembledText(revealedChunks(state)) || NOTHING_YET;
/** When the first non-empty piece of content arrived among `chunks`. */
export function firstWordsAt(chunks: readonly RecordedChunk[]): string {
  const first = chunks.find((chunk) => chunk.kind === 'content' && chunk.content.length > 0);
  return first ? secondsLabel(first.at) : NOT_YET;
}
/** When the chunk with finish_reason stop arrived. */
export function lastWordsAt(chunks: readonly RecordedChunk[]): string {
  const last = chunks.find((chunk) => chunk.kind === 'content' && chunk.finishReason === 'stop');
  return last ? secondsLabel(last.at) : NOT_YET;
}
export const usageArrived = (chunks: readonly RecordedChunk[]) =>
  chunks.some((chunk) => chunk.kind === 'usage');
/** The facts shown under the graphic: the times only, and a time only once it is known. */
export function eventsFacts(state: EventsState): [string, string][] {
  if (!state.streaming)
    return [
      ['First words at', secondsLabel(PLAIN_AT)],
      ['Last words at', secondsLabel(PLAIN_AT)],
    ];
  const chunks = revealedChunks(state);
  const facts: [string, string][] = [
    ['Chunks', `${state.revealed} of ${CHUNK_COUNT}`],
    ['First words at', firstWordsAt(chunks)],
    ['Last words at', lastWordsAt(chunks)],
  ];
  return facts.filter(([, value]) => value !== NOT_YET);
}
export const PREDICT_FIRST_LINE = 'Press Next chunk. Predict what the first line carries.';
export const BLANK_MESSAGE = 'Nothing to show for 2.58 seconds, then everything at once.';
export const STREAMED_MESSAGE =
  'First words at 1.05 s, the whole reply by 1.50 s, usage in the last chunk.';
export const ROLE_MESSAGE = 'The first chunk carries the role and empty content. No words yet.';
export const STOP_MESSAGE = 'finish_reason is stop: the words are complete at 1.49 s.';
export function eventsSummary(state: EventsState): Summary {
  if (!state.streaming) return { tone: 'warning', message: BLANK_MESSAGE };
  if (state.revealed === 0) return { tone: 'neutral', message: PREDICT_FIRST_LINE };
  const chunks = revealedChunks(state);
  if (usageArrived(chunks)) return { tone: 'success', message: STREAMED_MESSAGE };
  const last = chunks.at(-1);
  if (last?.kind !== 'content') return { tone: 'neutral', message: ROLE_MESSAGE };
  if (last.finishReason === 'stop') return { tone: 'neutral', message: STOP_MESSAGE };
  return {
    tone: 'neutral',
    message: `Chunk ${state.revealed} of ${CHUNK_COUNT} at ${secondsLabel(last.at)}: ${assembledText(chunks).length} characters on the page.`,
  };
}

// --- stream-relay: which connection writes the row -------------------------------------------

export const RELAY_DESIGNS = ['request', 'own'] as const;
export type RelayDesign = (typeof RELAY_DESIGNS)[number];
export const relayDesignLabels: Record<RelayDesign, string> = {
  request: "The request's connection",
  own: 'A connection the stream opens',
};
export const toRelayDesign = (value: string): RelayDesign =>
  RELAY_DESIGNS.find((design) => design === value) ?? 'request';
/** The three pieces of the Workshop 3.4 development project's digest, exactly as relayed. */
export const DIGEST_PIECES: readonly [string, string, string] = [
  'The demo preparation is finished. Ravi is currently responsible for writing the release note, while the remaining tasks of checking the dates, booking the room,',
  ' and ordering name badges are still open and need to be assigned. No specific blockers or dependencies are noted, so the team can proceed with',
  ' these outstanding items.',
];
export const END_OK = '{"outcome": "ok", "error": null}';
export const RELAY_STEPS: readonly string[] = [
  'The route returns a streaming response',
  "The request's connection commits and closes",
  `Piece 1 reaches the browser: '${DIGEST_PIECES[0]}'`,
  `Piece 2 reaches the browser: '${DIGEST_PIECES[1]}'`,
  `Piece 3 reaches the browser: '${DIGEST_PIECES[2]}'`,
  `The end event is sent: ${END_OK}`,
  'The stream writes its model_calls row',
];
export const RELAY_STEP_COUNT = RELAY_STEPS.length;
/** version | outcome | prompt tokens | completion tokens | latency ms, as the recorded row reads. */
export const RELAY_ROW = 'digest-v1 | ok | 115 | 62 | 1629';
export const CLOSED_ERROR = 'psycopg.OperationalError: the connection is closed';
export const NOT_WRITTEN = 'not written yet';
export interface RelayState {
  design: RelayDesign;
  /** Steps taken so far, 0 to 7. */
  step: number;
}
export const initialRelay = (): RelayState => ({ design: 'request', step: 0 });
export type RelayAction =
  { type: 'design'; value: RelayDesign } | { type: 'next' } | { type: 'reset' };
export const canStepRelay = (state: RelayState) => state.step < RELAY_STEP_COUNT;
/** Changing the design keeps the step: the two designs differ only at the write. */
export function relayTransition(state: RelayState, action: RelayAction): RelayState {
  switch (action.type) {
    case 'reset':
      return initialRelay();
    case 'design':
      return { ...state, design: action.value };
    case 'next':
      return canStepRelay(state) ? { ...state, step: state.step + 1 } : state;
  }
}
export const revealedSteps = (state: RelayState): readonly string[] =>
  RELAY_STEPS.slice(0, state.step);
/** How many pieces the browser shows after `step` steps: the pieces arrive at steps 3, 4 and 5. */
export const piecesShown = (step: number) => Math.min(Math.max(step - 2, 0), DIGEST_PIECES.length);
export const pieceCount = (count: number) => `${count} ${count === 1 ? 'piece' : 'pieces'}`;
export const requestConnectionLabel = (step: number) => (step >= 2 ? 'closed at step 2' : 'open');
export const relayRowWritten = (state: RelayState) =>
  state.step === RELAY_STEP_COUNT && state.design === 'own';
export function relayRowText(state: RelayState): string {
  if (state.step < RELAY_STEP_COUNT) return NOT_WRITTEN;
  return state.design === 'own' ? RELAY_ROW : CLOSED_ERROR;
}
export const PREDICT_CONNECTION =
  'Press Next step. Predict which connection is still open when the row is written.';
export const CLOSED_MESSAGE = "The request's connection closed at step 2, so no row was written.";
export const OWN_CONNECTION_MESSAGE =
  'The stream opened its own connection in finally, so the row was written.';
export function relaySummary(state: RelayState): Summary {
  if (state.step === 0) return { tone: 'neutral', message: PREDICT_CONNECTION };
  if (state.step < RELAY_STEP_COUNT)
    return {
      tone: 'neutral',
      message: `Step ${state.step} of ${RELAY_STEP_COUNT}: the browser shows ${pieceCount(piecesShown(state.step))}.`,
    };
  return state.design === 'own'
    ? { tone: 'success', message: OWN_CONNECTION_MESSAGE }
    : { tone: 'warning', message: CLOSED_MESSAGE };
}

// --- stream-failure: four endings and what each leaves behind --------------------------------

export const ENDINGS = ['finishes', 'cut', 'error', 'abandoned'] as const;
export type Ending = (typeof ENDINGS)[number];
export const endingLabels: Record<Ending, string> = {
  finishes: 'It finishes',
  cut: 'The ceiling cuts it at 60 tokens',
  error: 'The model fails after two pieces',
  abandoned: 'The browser leaves after one piece',
};
export const toEnding = (value: string): Ending =>
  ENDINGS.find((ending) => ending === value) ?? 'finishes';
export type Outcome = 'ok' | 'cut' | 'error';
export const MODEL_503 = 'Model error 503: The model provider is unavailable. Try again shortly.';
export const BROWSER_LEFT = 'The browser stopped reading before the digest ended.';
export const TAB_CLOSED = 'the tab was closed';
export const NO_END_EVENT = 'no end event: nobody is listening';
export const NO_USAGE = 'no usage';
export interface EndingRecord {
  /** How many of the three pieces reached the page. */
  pieces: number;
  outcome: Outcome;
  /** The error column of the row, when finally had one to write. */
  error: string | null;
  /** The data of the end event, or null when nobody was listening. */
  endEvent: string | null;
  /** Prompt and completion tokens from the usage chunk, when one arrived. */
  usage: readonly [number, number] | null;
  /** What the page saw after its last piece, when the digest did not finish. */
  note: string | null;
}
export const endings: Record<Ending, EndingRecord> = {
  finishes: {
    pieces: 3,
    outcome: 'ok',
    error: null,
    endEvent: END_OK,
    usage: [115, 62],
    note: null,
  },
  cut: {
    pieces: 2,
    outcome: 'cut',
    error: null,
    endEvent: '{"outcome": "cut", "error": null}',
    usage: [115, 60],
    note: "finish_reason is length: the reply stops at 'proceed with'",
  },
  error: {
    pieces: 2,
    outcome: 'error',
    error: MODEL_503,
    endEvent: `{"outcome": "error", "error": "${MODEL_503}"}`,
    usage: null,
    note: 'the model failed: no third piece',
  },
  abandoned: {
    pieces: 1,
    outcome: 'cut',
    error: BROWSER_LEFT,
    endEvent: null,
    usage: null,
    note: TAB_CLOSED,
  },
};
/** The reply the row stores: the pieces that were sent, joined. */
export const replyText = (pieces: number) => DIGEST_PIECES.slice(0, pieces).join('');
export const replyLength = (pieces: number) => replyText(pieces).length;
export const usageCell = (usage: readonly [number, number] | null) =>
  usage ? `${usage[0]} / ${usage[1]} tokens` : NO_USAGE;
/** version | outcome | reply length | usage. */
export function endingRow(ending: Ending): string {
  const record = endings[ending];
  return `digest-v1 | ${record.outcome} | reply ${replyLength(record.pieces)} characters | ${usageCell(record.usage)}`;
}
/** The Row panel: the row, then its error column when finally wrote one. */
export function endingRowText(ending: Ending): string {
  const record = endings[ending];
  return record.error ? `${endingRow(ending)}\nerror: ${record.error}` : endingRow(ending);
}
/** The Pieces panel: one line per piece the page received, then the note. */
export function piecesText(ending: Ending): string {
  const record = endings[ending];
  const lines = DIGEST_PIECES.slice(0, record.pieces).map(
    (piece, index) => `Piece ${index + 1}: '${piece}'`,
  );
  return [...lines, ...(record.note ? [record.note] : [])].join('\n');
}
/** The End event panel: the event as the browser reads it, or why none came. */
export function endEventText(ending: Ending): string {
  const record = endings[ending];
  return record.endEvent ? `event: end\ndata: ${record.endEvent}` : NO_END_EVENT;
}
export interface FailureState {
  ending: Ending;
  played: boolean;
}
export const initialFailure = (): FailureState => ({ ending: 'finishes', played: false });
export type FailureAction =
  { type: 'ending'; value: Ending } | { type: 'play' } | { type: 'reset' };
export function failureTransition(state: FailureState, action: FailureAction): FailureState {
  switch (action.type) {
    case 'reset':
      return initialFailure();
    case 'ending':
      return { ending: action.value, played: false };
    case 'play':
      return { ...state, played: true };
  }
}
export const NOT_PLAYED = 'not played yet';
export const PREDICT_ENDING = 'Predict the end event and the row, then press Play.';
export const endingMessages: Record<Ending, Summary> = {
  finishes: {
    tone: 'success',
    message: 'Finished: the end event says ok and the row has the usage.',
  },
  cut: {
    tone: 'warning',
    message: 'The ceiling stopped it: the end event and the row both say cut.',
  },
  error: {
    tone: 'warning',
    message: 'The status was already 200, so only the end event and the row report the failure.',
  },
  abandoned: {
    tone: 'neutral',
    message: 'No end event because nobody was listening, but finally still wrote the row.',
  },
};
export const failureSummary = (state: FailureState): Summary =>
  state.played ? endingMessages[state.ending] : { tone: 'neutral', message: PREDICT_ENDING };

// --- stream-choice: which features should stream ---------------------------------------------

export const FEATURES = ['digest', 'suggestions', 'yes-no', 'report'] as const;
export type Feature = (typeof FEATURES)[number];
export const featureLabels: Record<Feature, string> = {
  digest: 'The project digest (prose)',
  suggestions: 'Task suggestions (JSON)',
  'yes-no': 'A yes-or-no question',
  report: 'A long report (prose)',
};
export const toFeature = (value: string): Feature =>
  FEATURES.find((feature) => feature === value) ?? 'digest';
export const CHOICE_TIMES: readonly [string, string, string] = ['0.5 s', '1.0 s', '1.5 s'];
export const JSON_FRAGMENT = '{"tasks": [{"title": "Book the';
export const NOT_A_LIST = 'not a list yet';
export const YES = 'Yes.';
export const REPORT_FIRST_SENTENCE = 'The launch is on track for the 12th.';
export const TWO_PARAGRAPHS = 'two paragraphs so far';
export const DONE = '(done)';
const done = (text: string) => `${text} ${DONE}`;
export const featureGains: Record<Feature, string> = {
  digest: 'words at 1.0 s, not 2.6 s',
  suggestions: 'nothing usable',
  'yes-no': 'none',
  report: 'reading at 1.0 s, not 9.8 s',
};
export interface ChoiceView {
  /** What the page can show at 0.5 s, 1.0 s and 1.5 s. */
  cells: readonly [string, string, string];
  doneAt: string;
  gain: string;
}
const NOTHING_THREE: readonly [string, string, string] = [NOTHING_YET, NOTHING_YET, NOTHING_YET];
export function choiceView(feature: Feature, streaming: boolean): ChoiceView {
  const gain = featureGains[feature];
  switch (feature) {
    case 'digest':
      return streaming
        ? { cells: [NOTHING_YET, DIGEST_FIRST_PIECE, done(STREAMED_DIGEST)], doneAt: '1.5 s', gain }
        : { cells: NOTHING_THREE, doneAt: '2.6 s', gain };
    case 'suggestions':
      return {
        cells: [
          NOTHING_YET,
          streaming ? `${JSON_FRAGMENT} (${NOT_A_LIST})` : NOTHING_YET,
          done(JSON_REPLY),
        ],
        doneAt: '1.5 s',
        gain,
      };
    case 'yes-no':
      return { cells: [NOTHING_YET, done(YES), YES], doneAt: '1.0 s', gain };
    case 'report':
      return streaming
        ? { cells: [NOTHING_YET, REPORT_FIRST_SENTENCE, TWO_PARAGRAPHS], doneAt: '9.8 s', gain }
        : { cells: NOTHING_THREE, doneAt: '9.8 s', gain };
  }
}
export interface ChoiceState {
  feature: Feature;
  streaming: boolean;
}
export const initialChoice = (): ChoiceState => ({ feature: 'digest', streaming: true });
export const PROSE_MESSAGE = 'Prose can be read as it arrives: words at 1.0 s.';
export const FRAGMENT_MESSAGE =
  'A JSON fragment is not a list, so nothing shows until the last brace.';
export const ONE_TOKEN_MESSAGE = 'One token: nothing to stream.';
export function choiceSummary(state: ChoiceState): Summary {
  if (state.feature === 'yes-no') return { tone: 'neutral', message: ONE_TOKEN_MESSAGE };
  if (!state.streaming)
    return {
      tone: 'neutral',
      message: `Nothing until the end: ${choiceView(state.feature, false).doneAt}.`,
    };
  if (state.feature === 'suggestions') return { tone: 'warning', message: FRAGMENT_MESSAGE };
  return { tone: 'success', message: PROSE_MESSAGE };
}
